/**
 * PRP-WEB-1 W2.6: the project page's "Notes & photos" tab. The phone's "Room created" markers
 * are dropped (the scope builder's own filter, V2ScopeBuilderPage), notes are grouped by room
 * (a note's `room_id` → the room's name from its marker), newest first within a room, notes with
 * no room last. Photos are a thumbnail grid; a thumbnail opens the photo full size.
 * Note text is data, shown as text.
 * Photos load from the note's short-lived signed `image_url` (B-1244); an expired or missing link
 * is fetched once from `/image-url`.
 */
import { useEffect, useRef, useState } from 'react';

import { BackendAPI } from '../../lib/api';
import type { FieldNote } from '../../types';
import { EmptyState } from './ui/empty-state';
import { Modal } from './ui/modal';
import { IconLayers } from './icons';

/** The phone writes this note when a room is added; it carries the geometry, not a finding. */
export const ROOM_CREATED_MARKER = 'Room created';

/** True for the auto "Room created" marker (same test as V2ScopeBuilderPage's notes prefill). */
export function isRoomCreatedMarker(n: Pick<FieldNote, 'content'>): boolean {
  return (n.content ?? '').trim() === ROOM_CREATED_MARKER;
}

/** A note worth showing: not a marker, and a photo or a note with text. */
export function isShownNote(n: FieldNote): boolean {
  if (isRoomCreatedMarker(n)) return false;
  return n.type === 'photo' || !!(n.content ?? '').trim();
}

const NO_ROOM = '';
const when = (n: FieldNote) => n.created_at || n.time || '';

export interface NotesRoomGroup {
  roomId: string;
  name: string;
  notes: FieldNote[];
}

/** Shown notes grouped by room: rooms by name, notes newest first, the no-room group last. */
export function groupNotesByRoom(notes: FieldNote[]): NotesRoomGroup[] {
  const names = new Map<string, string>();
  for (const n of notes) {
    if (!n.room_id) continue;
    const g = n.room_geometry as { name?: string } | null | undefined;
    const name = (n.room_type || g?.name || '').trim();
    if (name && !names.has(n.room_id)) names.set(n.room_id, name);
  }
  const groups = new Map<string, NotesRoomGroup>();
  for (const n of notes.filter(isShownNote)) {
    const key = n.room_id || NO_ROOM;
    let g = groups.get(key);
    if (!g) {
      g = { roomId: key, name: key ? names.get(key) || 'Unnamed room' : 'Not in a room', notes: [] };
      groups.set(key, g);
    }
    g.notes.push(n);
  }
  const out = [...groups.values()];
  for (const g of out) g.notes.sort((a, b) => when(b).localeCompare(when(a)));
  return out.sort((a, b) => {
    if (!a.roomId !== !b.roomId) return a.roomId ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

function NoteRow({ n }: { n: FieldNote }) {
  return (
    <li className="rounded-lg border border-[var(--bd)] bg-[var(--bg-1)] p-3">
      <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wide text-[var(--tx-faint)]">
        <span>{n.type === 'voice' ? 'Voice' : 'Note'}</span>
        {n.duration && <span>· {n.duration}</span>}
        {when(n) && <span>· {when(n).slice(0, 10)}</span>}
      </div>
      <p className="whitespace-pre-wrap break-words text-[13px] text-[var(--tx-hi)]">{n.content}</p>
    </li>
  );
}

/** The notes list adds a 5-minute signed link (and its expiry) to each photo note (B-1244). */
export type FieldPhotoNote = FieldNote & { image_url?: string | null; image_url_expires_at?: string | null };

/**
 * A photo note's image from its signed link. A link that fails (5-minute life) or was never
 * signed (past the list's first 50) is fetched from `/image-url`, at most once a minute: a fresh
 * link that fails at once is broken and left alone; one that fails later has expired with the
 * page left open, and is fetched again (review I4).
 */
export function NotePhoto({ projectId, note, className, lazy }: {
  projectId: string; note: FieldPhotoNote; className: string; lazy?: boolean;
}) {
  const [src, setSrc] = useState<string | null>(note.image_url ?? null);
  const refetchedAt = useRef<number | null>(null);

  const refetch = () => {
    if (refetchedAt.current !== null && Date.now() - refetchedAt.current < 60_000) return;
    refetchedAt.current = Date.now();
    BackendAPI.getNoteImageUrl(projectId, note.id)
      .then((r) => setSrc(r.url))
      .catch(() => setSrc(null));
  };

  useEffect(() => {
    if (!note.image_url) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!src) return <div className={className} aria-label={note.content || 'Field photo'} role="img" />;
  return (
    <img
      src={src}
      alt={note.content || 'Field photo'}
      className={className}
      loading={lazy ? 'lazy' : undefined}
      onError={refetch}
    />
  );
}

/** The Notes & photos tab body. */
export function FieldNotesPanel({ projectId, notes }: { projectId: string; notes: FieldNote[] }) {
  const [photo, setPhoto] = useState<FieldNote | null>(null);
  const groups = groupNotesByRoom(notes);

  if (groups.length === 0) {
    return (
      <EmptyState icon={<IconLayers />} title="No notes or photos yet" description="Notes and photos taken on the phone show here." />
    );
  }

  return (
    <div data-testid="field-notes">
      {groups.map((g) => {
        const photos = g.notes.filter((n) => n.type === 'photo');
        const texts = g.notes.filter((n) => n.type !== 'photo');
        return (
          <section key={g.roomId || 'none'} className="mb-5" data-testid={`notes-room-${g.roomId || 'none'}`}>
            <h2 className="mb-2 text-[13px] font-[560] uppercase tracking-wide text-[var(--tx-lo)]">
              {g.name} <span className="text-[var(--tx-faint)]">({g.notes.length})</span>
            </h2>
            {photos.length > 0 && (
              <div className="mb-2 grid grid-cols-[repeat(auto-fill,minmax(112px,1fr))] gap-2">
                {photos.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => setPhoto(n)}
                    className="aspect-square overflow-hidden rounded-md border border-[var(--bd)] bg-[var(--bg-1)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]"
                    aria-label={`Open photo: ${n.content || 'field photo'}`}
                  >
                    <NotePhoto projectId={projectId} note={n} className="h-full w-full object-cover" lazy />
                  </button>
                ))}
              </div>
            )}
            {texts.length > 0 && (
              <ul className="flex flex-col gap-2">
                {texts.map((n) => (
                  <NoteRow key={n.id} n={n} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
      {photo && (
        <Modal label="Photo" title={photo.content || 'Field photo'} onClose={() => setPhoto(null)} className="max-w-4xl" testId="photo-full">
          <NotePhoto projectId={projectId} note={photo} className="mx-auto max-h-[75vh] w-auto max-w-full object-contain" />
        </Modal>
      )}
    </div>
  );
}
